/** RFC 0345 S2. A passkey PRF unlocks an independent Ed25519 recovery seed.
 * Only the envelope is uploaded. Never send the PRF output or seed to a server.
 * https://www.w3.org/TR/webauthn-3/#prf-extension
 */
const encoder = new TextEncoder()
const DOMAIN = 'mod:sharing:recovery:v1'

/** Use this input for WebAuthn extensions.prf.eval.first on creation/assertion. */
export function recoveryPrfInput(): Uint8Array<ArrayBuffer> {
  return encoder.encode(DOMAIN)
}

export interface RecoveryIdentity {
  /** The authenticated account, not an email entered by the caller. */
  accountId: string
  rpId: string
  credentialId: string
}

export interface RecoveryEnvelope extends RecoveryIdentity {
  version: 1 | 2
  /** Hex-encoded public HKDF salt, GCM nonce, and authenticated ciphertext. */
  salt: string
  iv: string
  ciphertext: string
}

function hex(bytes: Uint8Array): string {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join(
    '',
  )
}

function bytes(value: string, length: number): Uint8Array<ArrayBuffer> {
  if (
    typeof value !== 'string' ||
    !/^[0-9a-f]+$/.test(value) ||
    value.length !== length * 2
  ) {
    throw new Error('Invalid recovery envelope')
  }
  return Uint8Array.from(value.match(/../g) ?? [], (pair) =>
    Number.parseInt(pair, 16),
  )
}

function context(
  identity: RecoveryIdentity,
  version: 1 | 2 = 1,
): Uint8Array<ArrayBuffer> {
  for (const value of [
    identity.accountId,
    identity.rpId,
    identity.credentialId,
  ]) {
    if (
      typeof value !== 'string' ||
      value.length === 0 ||
      value.length > 2048
    ) {
      throw new Error('Invalid recovery identity')
    }
  }
  return encoder.encode(
    JSON.stringify([
      DOMAIN,
      identity.accountId,
      identity.rpId,
      identity.credentialId,
      ...(version === 2 ? [2] : []),
    ]),
  )
}

async function wrappingKey(
  prf: Uint8Array,
  salt: Uint8Array<ArrayBuffer>,
  info: Uint8Array<ArrayBuffer>,
) {
  if (prf.byteLength !== 32)
    throw new Error('Passkey PRF output is required for recovery')
  const material = await crypto.subtle.importKey(
    'raw',
    new Uint8Array(prf),
    'HKDF',
    false,
    ['deriveKey'],
  )
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt, info },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

/** Call only after a PRF-capable passkey assertion. Unsupported authenticators
 * must fail visibly; an authentication signature is not an encryption secret. */
async function sealBytes(
  seed: Uint8Array,
  prf: Uint8Array,
  identity: RecoveryIdentity,
  version: 1 | 2 = 1,
): Promise<RecoveryEnvelope> {
  const binding = context(identity, version)
  const salt = crypto.getRandomValues(new Uint8Array(32))
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const key = await wrappingKey(prf, salt, binding)
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: binding, tagLength: 128 },
    key,
    new Uint8Array(seed),
  )
  return {
    version,
    accountId: identity.accountId,
    rpId: identity.rpId,
    credentialId: identity.credentialId,
    salt: hex(salt),
    iv: hex(iv),
    ciphertext: hex(new Uint8Array(ciphertext)),
  }
}

/** expected comes from the authenticated account and selected credential,
 * independently of the server's envelope. Switching accounts cannot reuse it. */
async function openBytes(
  envelope: RecoveryEnvelope,
  prf: Uint8Array,
  expected: RecoveryIdentity,
): Promise<Uint8Array<ArrayBuffer>> {
  const binding = context(expected, envelope.version)
  if (
    (envelope.version !== 1 && envelope.version !== 2) ||
    envelope.accountId !== expected.accountId ||
    envelope.rpId !== expected.rpId ||
    envelope.credentialId !== expected.credentialId
  ) {
    throw new Error('Recovery backup belongs to a different account or passkey')
  }
  const key = await wrappingKey(prf, bytes(envelope.salt, 32), binding)
  const plaintext = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: bytes(envelope.iv, 12),
      additionalData: binding,
      tagLength: 128,
    },
    key,
    bytes(envelope.ciphertext, envelope.ciphertext.length / 2),
  )
  return new Uint8Array(plaintext)
}

/** Back up both the signer and private encryption prekeys. Keep the recovery
 * member offline after enrollment, so these backed-up prekeys do not rotate.
 * Public archives and signed events sync separately.
 */
export interface RecoveryMaterial {
  seed: Uint8Array
  prekeySecrets: Uint8Array
  /** Encrypted bootstrap locations, never visible to the backup server. */
  location?: { personId: string; worldId: string }
}
function locationBytes(location: NonNullable<RecoveryMaterial['location']>) {
  if (
    !/^[0-9a-f]{64}$/.test(location.personId) ||
    typeof location.worldId !== 'string' ||
    !location.worldId.length ||
    location.worldId.length > 256
  )
    throw new Error('Invalid recovery location')
  return encoder.encode(JSON.stringify(location))
}
export async function sealRecoveryMaterial(
  material: RecoveryMaterial,
  prf: Uint8Array,
  identity: RecoveryIdentity,
): Promise<RecoveryEnvelope> {
  if (
    material.seed.byteLength !== 32 ||
    !material.prekeySecrets.byteLength ||
    material.prekeySecrets.byteLength > 16384
  )
    throw new Error('Invalid recovery key material')
  const location = material.location
    ? locationBytes(material.location)
    : undefined
  const prefix = location ? 36 : 32
  const payload = new Uint8Array(
    prefix + material.prekeySecrets.byteLength + (location?.byteLength ?? 0),
  )
  payload.set(material.seed)
  if (location)
    new DataView(payload.buffer).setUint32(
      32,
      material.prekeySecrets.byteLength,
    )
  payload.set(material.prekeySecrets, prefix)
  if (location)
    payload.set(location, prefix + material.prekeySecrets.byteLength)
  try {
    return await sealBytes(payload, prf, identity, location ? 2 : 1)
  } finally {
    payload.fill(0)
  }
}
export async function openRecoveryMaterial(
  envelope: RecoveryEnvelope,
  prf: Uint8Array,
  identity: RecoveryIdentity,
): Promise<RecoveryMaterial> {
  if (
    typeof envelope.ciphertext !== 'string' ||
    envelope.ciphertext.length < 98 ||
    envelope.ciphertext.length > 36000
  )
    throw new Error('Invalid recovery envelope size')
  const payload = await openBytes(envelope, prf, identity)
  try {
    if (envelope.version === 1) {
      if (payload.length > 16416)
        throw new Error('Invalid recovery material size')
      return { seed: payload.slice(0, 32), prekeySecrets: payload.slice(32) }
    }
    if (payload.length < 38) throw new Error('Invalid recovery material')
    const length = new DataView(
      payload.buffer,
      payload.byteOffset,
      payload.byteLength,
    ).getUint32(32)
    if (!length || length > 16384 || 36 + length >= payload.length)
      throw new Error('Invalid recovery material')
    const location = JSON.parse(
      new TextDecoder('utf-8', { fatal: true }).decode(
        payload.subarray(36 + length),
      ),
    )
    if (!location || typeof location !== 'object')
      throw new Error('Invalid recovery location')
    locationBytes(location)
    return {
      seed: payload.slice(0, 32),
      prekeySecrets: payload.slice(36, 36 + length),
      location: { personId: location.personId, worldId: location.worldId },
    }
  } finally {
    payload.fill(0)
  }
}
